
import { useCallback, useEffect, useMemo, useState } from 'react';
import styles from './Handbook.module.scss';
import { Document, Page } from 'react-pdf';
import 'react-pdf/dist/Page/TextLayer.css';
import Spinner from '@src/components/spinner/Spinner';
import Modal from '@src/components/modal/Modal';
import Actions from '@src/components/program-details/selected-content/actions/Actions';
import Button from '@src/components/button/Button';
import DownloadIcon from 'src/assets/icons/download.svg?react';
import PrintIcon from 'src/assets/icons/print.svg?react';
import { logger } from '../../../utils/logger';

type Props = {
    open: boolean;
    onClose: () => void;
    filePath: string;
}

const roundScale = (num: number) => Math.round(num * 100) / 100;

function Handbook(props: Props) {
    const { open, filePath, onClose } = props


    const [totalPages, setTotalPages] = useState(0)
    const [pageNumber, setPageNumber] = useState(1)
    const [downloading, setDownloading] = useState(false);
    const [PDFViewerOneError, setPDFViewerOneError] = useState(false);
    const [scale, setScale] = useState(1);

    useEffect(() => {
        fetch(filePath)
            .then(resp => resp.blob())
            .then(blob => {
                const newPDFBlob = new Blob([blob], {type: 'application/pdf'});
                const localUrl = window.URL.createObjectURL(newPDFBlob);
                const iframeEle = document.getElementById("hiddenFileHackForPrint");
                if (iframeEle) {
                // @ts-ignore
                iframeEle.src = localUrl;
                }
                window.URL.revokeObjectURL(localUrl);
            }).catch((e) => logger.info(e))
    }, [filePath])
    
    const printFile = () => {
        let getPrintEle = document.getElementById("hiddenFileHackForPrint");
    
        if (getPrintEle?.getAttribute("src") === null) {
          throw new Error('PDF not loaded');
        }
        (getPrintEle as HTMLIFrameElement)?.contentWindow?.print();
    }

    const increaseScale = useCallback(() => {
        const newScale = Math.min(scale + 0.1, 2);
        setScale(roundScale(newScale));
    }, [scale]);
    
      const decreaseScale = useCallback(() => {
        const newScale = Math.max(scale - 0.1, 0.1);
        setScale(roundScale(newScale));
    }, [scale]);

    const onDocumentLoadSuccess = useCallback(({ numPages }) => {
        setTotalPages(numPages);
    }, [setTotalPages]);

    const handlePageNumber = useCallback((value: any) => {
        setPageNumber(value);
    }, [setPageNumber]);

    const handleLeft = useCallback(() => {
        if (pageNumber) {
            handlePageNumber(Math.max(pageNumber - 1, 1));
        }
    }, [pageNumber, handlePageNumber]);
    
    const handleRight = useCallback(() => {
        if (pageNumber) {
            handlePageNumber(Math.min(pageNumber + 1, totalPages));
        }
    }, [totalPages, pageNumber, handlePageNumber]);

    const prevButton = useMemo(() => {
        if (totalPages === 0) return null
        if (pageNumber === 1) return null;
        return <Button size={'extraSmall'} onClick={handleLeft} label={'Previous'}/>;
    }, [totalPages, pageNumber, handleLeft]);
    
    const nextButton = useMemo(() => {
        if (totalPages === 0) return null
        if (pageNumber === totalPages) return null;
        return <Button size={'extraSmall'} onClick={handleRight} label={'Next'}/>;
    }, [handleRight, pageNumber, totalPages]);

    async function downloadFile(name: string, _url: string) {
        if (!_url) {
          throw new Error('Url not provided');
        }
        return fetch(_url)
          .then(resp => resp.blob())
          .then(blob => {
            const localUrl = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.style.display = 'none';
            a.href = localUrl;
      
            const regex = /\.\w+$/gm;
            const defName = `${name}.pdf`;
            a.download = regex.test(_url) ? _url.split('/').pop() ?? defName : defName;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(localUrl);
          })
          .catch(e => {
            logger.debug(`[e]`, e);
            throw e;
          });
    }

    const handleDownload = useCallback(() => {
        setDownloading(true);
        downloadFile('handbook', filePath)
        .catch(() => {
            alert(`Can't download file.`);
        })
        .finally(() => {
            setDownloading(false);
        });
    }, [filePath]);

    return (
        <Modal global={false} open={open} onClose={onClose} closeButton={false} className={styles.wrapper}>
            <Actions fullScreen={false} leftButton={prevButton} rightButton={nextButton} 
                totalPages={totalPages} pageNumber={pageNumber} className={styles.actionsWrapper} 
                hideNextButton={pageNumber === totalPages} isPreview={true} fromModal={true}>
                <iframe id="hiddenFileHackForPrint" title="hiddenFileHackForPrint" style={{display: "none"}}/>
                {totalPages !== 0 && <div className={styles.toolbar}>
                    <button disabled={downloading} onClick={handleDownload} className={styles.toolbarBtn}>
                    <DownloadIcon/>
                    </button>
                    <button className={styles.toolbarBtn} onClick={printFile}><PrintIcon/></button>
                    {!PDFViewerOneError && <>
                    <button className={styles.toolbarBtn} onClick={decreaseScale}>-</button>
                    <button disabled={scale === 1} className={styles.toolbarBtn} onClick={increaseScale}>+</button>
                    <div className={styles.scaleTitle}>{Number(scale * 100).toFixed(0)}%</div>
                    </>}
                </div>}
                <Document
                    file={filePath}
                    onLoadError={() => {
                        setPDFViewerOneError(true);
                    }}
                    onLoadSuccess={onDocumentLoadSuccess}
                    className={styles.document}
                    loading={<Spinner/>}
                >
                    <Page pageNumber={pageNumber} height={window.innerHeight - 120} scale={scale}/>
            </Document>
          </Actions>
        </Modal>
    )
}

export default Handbook
